/**
 * Collection showcase — 3D coverflow carousel.
 *
 * Mirrors the reference site's Swiper coverflow: cards are laid out in a plain
 * flex row, then each is pushed back in Z by a fixed step times its distance
 * from the centre. The viewport's perspective does the rest, producing the
 * shrink, the inward drift and the overlap in one go.
 *
 * Position is a continuous index rather than a slide number, so a drag moves
 * the whole cascade smoothly and only settles onto a card when the finger
 * lifts. Layout maths comes from offsetLeft, which transforms do not affect.
 */
(() => {
  const DEPTH = 256;

  const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

  const init = (root) => {
    if (root.dataset.ctShowcase === 'done') return;

    const viewport = root.querySelector('.ct-showcase__viewport');
    const track = root.querySelector('.ct-showcase__track');
    const items = [...root.querySelectorAll('.ct-showcase__item')];
    if (!viewport || !track || items.length === 0) return;

    const dotsWrap = root.querySelector('.ct-showcase__dots');
    const prev = root.querySelector('.ct-showcase__arrow--prev');
    const next = root.querySelector('.ct-showcase__arrow--next');
    const last = items.length - 1;

    let progress = Math.floor(last / 2);
    let pitch = 1;
    let cardW = 0;
    let settled = -1;

    const dots = items.map((item, i) => {
      const d = document.createElement('button');
      d.type = 'button';
      d.className = 'ct-showcase__dot';
      d.setAttribute('role', 'tab');
      d.setAttribute('aria-label', `Go to slide ${i + 1} of ${items.length}`);
      d.addEventListener('click', () => goTo(i));
      dotsWrap?.appendChild(d);
      return d;
    });

    const measure = () => {
      cardW = items[0].offsetWidth;
      pitch = items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : cardW;
      if (!pitch) pitch = cardW || 1;
    };

    const render = () => {
      const offset = (viewport.clientWidth - cardW) / 2 - progress * pitch;
      track.style.transform = `translate3d(${offset}px, 0, 0)`;

      items.forEach((item, i) => {
        const k = i - progress;
        const away = Math.abs(k);
        item.style.transform = `translate3d(0, 0, ${-DEPTH * away}px)`;
        item.style.zIndex = String(items.length - Math.round(away));
        item.style.setProperty('--ct-sh-l', clamp(k, 0, 1).toFixed(3));
        item.style.setProperty('--ct-sh-r', clamp(-k, 0, 1).toFixed(3));
      });

      const nearest = clamp(Math.round(progress), 0, last);
      if (nearest !== settled) {
        settled = nearest;
        items.forEach((item, i) => item.classList.toggle('is-active', i === nearest));
        dots.forEach((d, i) => {
          d.classList.toggle('is-active', i === nearest);
          d.setAttribute('aria-selected', i === nearest ? 'true' : 'false');
        });
      }
      if (prev) prev.disabled = progress <= 0.01;
      if (next) next.disabled = progress >= last - 0.01;
    };

    const goTo = (i) => {
      progress = clamp(i, 0, last);
      render();
    };

    /* ---- drag ---- */
    let dragging = false;
    let startX = 0;
    let startProgress = 0;
    let moved = 0;

    viewport.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      /* The arrows live inside the viewport. Starting a drag from one would
         take pointer capture on the viewport, and a captured pointer sends
         the follow-up mouse events to the capturing element instead of the
         button - so the button's click never fires and the arrow does
         nothing. Leave the press alone and let it be a click. */
      if (e.target.closest && e.target.closest('.ct-showcase__arrow')) return;
      dragging = true;
      moved = 0;
      startX = e.clientX;
      startProgress = progress;
      root.classList.add('is-dragging');
      viewport.setPointerCapture?.(e.pointerId);
    });

    viewport.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      moved = Math.max(moved, Math.abs(dx));
      progress = clamp(startProgress - dx / pitch, 0, last);
      render();
    });

    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      root.classList.remove('is-dragging');
      goTo(Math.round(progress));
    };

    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('lostpointercapture', endDrag);

    // A drag that ends on a card must not also follow its link.
    viewport.addEventListener(
      'click',
      (e) => {
        /* Swallow the click that ends a drag, so releasing over a card does
           not open it. `moved` still holds the last drag's distance, so the
           arrows have to be excluded or the first arrow press after any
           drag gets eaten too. */
        if (moved > 6 && !(e.target.closest && e.target.closest('.ct-showcase__arrow'))) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );

    prev?.addEventListener('click', () => goTo(Math.round(progress) - 1));
    next?.addEventListener('click', () => goTo(Math.round(progress) + 1));

    viewport.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { goTo(Math.round(progress) - 1); e.preventDefault(); }
      if (e.key === 'ArrowRight') { goTo(Math.round(progress) + 1); e.preventDefault(); }
    });

    window.addEventListener('resize', () => { measure(); render(); }, { passive: true });

    measure();
    render();
    requestAnimationFrame(() => root.classList.add('is-ready'));

    root.dataset.ctShowcase = 'done';
  };

  const boot = () => document.querySelectorAll('.ct-showcase').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();